import { ReactNode, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  /** Optional control(s) placed in the header, just left of the close button (e.g. a pin toggle). */
  headerAccessory?: ReactNode;
  /** Extra classes for the dialog panel (e.g. a wider `max-w-*`). */
  className?: string;
  /**
   * Where to send focus when the modal closes. Defaults to whatever was focused when it opened —
   * but that capture happens a commit after `open` flips true, so if the opener unmounts itself in
   * the same click (e.g. a menu row that closes its menu before opening this), the captured element
   * is already `<body>`. Pass the still-mounted trigger's ref to restore focus correctly.
   */
  returnFocusTo?: { readonly current: HTMLElement | null };
};

const CloseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/* Choreography ported from the owners-hub casino-settings Overlay (CASINO_SHEET tokens):
   the surface only ever SLIDES — up into place from below the viewport on open at EVERY
   breakpoint, down out of it on close — so a desktop modal arrives and leaves the same way it
   does as a phone sheet. Only the backdrop fades; the sheet's opacity is left untouched so it
   reads as leaving rather than fading away. */
const OPEN_MS = 384; // entrance — slow on purpose; a full surface that snaps in reads as a flash
const CLOSE_MS = 384; // exit, matched to the entrance so it leaves at the pace it arrived
const SETTLE_MS = 260; // snapping a half-dragged sheet back to rest — a correction, so quicker
const ENTER_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
/* Leaving eases IN (hold, then accelerate away) so the travel actually spends the duration on
   screen; the shared ease-OUT would front-load it and the sheet would be gone in a few frames. */
const EXIT_EASE = 'cubic-bezier(0.4, 0, 0.9, 0.55)';

// Drag-to-dismiss thresholds for the phone sheet.
const DISMISS_PX = 96; // past this many pixels of drag, let go rather than snap back…
const DISMISS_VELOCITY = 0.5; // …or, short of that, a flick this fast (px/ms)…
const FLICK_MIN_PX = 40; // …provided it actually travelled — a fast twitch is not a dismissal.

/* Below this width the modal is a bottom sheet (matches Tailwind's `sm`). The grab handle and
   its gesture bind only here, in lockstep with the CSS sheet form above. */
const SHEET_MQ = '(max-width: 639.98px)';

/**
 * The open-modal stack (oldest first), reactive so a modal can tell whether it is topmost (owns
 * Escape/Tab) and whether it is COVERED by another (recede behind it, so a stack reads as depth
 * rather than two flat surfaces). Module scope on purpose: modals portal to `<body>` from
 * anywhere in the tree, so there is no common ancestor to hold this.
 *
 * Each entry carries whether it has begun leaving, because the two questions the stack answers
 * part company during an exit: what a modal LOOKS like (the one behind rises back the moment the
 * top starts leaving, so the two read as one movement) vs. what it RESPONDS to (the one behind
 * must NOT answer Escape until the top is really gone).
 */
type StackEntry = { id: symbol; closing: boolean };
const stack: StackEntry[] = [];
const stackSubs = new Set<() => void>();
const notifyStack = () => stackSubs.forEach((fn) => fn());
const indexOfEntry = (id: symbol) => stack.findIndex((e) => e.id === id);
const pushModal = (id: symbol) => {
  if (indexOfEntry(id) === -1) stack.push({ id, closing: false });
  notifyStack();
};
const markClosing = (id: symbol) => {
  const at = indexOfEntry(id);
  if (at >= 0 && !stack[at].closing) {
    stack[at] = { id, closing: true };
    notifyStack();
  }
};
const removeModal = (id: symbol) => {
  const at = indexOfEntry(id);
  if (at >= 0) stack.splice(at, 1);
  notifyStack();
};
/** True when nothing sits above this modal (even a closing one): it owns the keyboard. */
const isTopModal = (id: symbol) => stack.length > 0 && stack[stack.length - 1].id === id;
/** True when a modal that is still arriving or resting (not leaving) covers this one. */
const isCoveredModal = (id: symbol) => {
  const at = indexOfEntry(id);
  if (at === -1) return false;
  return stack.slice(at + 1).some((e) => !e.closing);
};
const subscribeStack = (fn: () => void) => {
  stackSubs.add(fn);
  return () => {
    stackSubs.delete(fn);
  };
};

/** True while any modal is mounted (open or animating closed). Lets global keyboard
 *  shortcuts (e.g. undo/redo) stand down so they never fire behind a dialog. */
export const hasOpenModal = (): boolean => stack.length > 0;

/** How far a covered modal recedes on desktop, so the one on top reads as nearer. */
const COVERED_SCALE = 0.95;

/**
 * Background scroll lock, counted rather than saved per modal, so a stack (or a route change that
 * tears the whole stack down in one commit, where sibling cleanup order is not guaranteed) can't
 * strand the page locked: the first modal locks, the last unlocks.
 */
let scrollLockDepth = 0;
let scrollLockPrev = '';
const lockScroll = () => {
  if (scrollLockDepth === 0) {
    scrollLockPrev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockDepth += 1;
};
const unlockScroll = () => {
  scrollLockDepth = Math.max(0, scrollLockDepth - 1);
  if (scrollLockDepth === 0) document.body.style.overflow = scrollLockPrev;
};

/**
 * The surface's lifecycle. `preEnter` is the single below-the-viewport frame a fresh open is
 * placed at before it slides up; a reopen mid-exit skips it and reverses from where it is.
 * `closing` keeps the surface mounted (and its scroll-lock / focus-trap live) through the exit
 * slide until the timeout unmounts it.
 */
type Phase = 'closed' | 'preEnter' | 'open' | 'closing';

/** Subscribe to a media query and re-render when it flips. SSR-safe. */
const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

/**
 * Centered modal / mobile bottom-sheet — a Tailwind port of the owners-hub `Overlay`:
 * portal into body, dim backdrop, rounded surface with a pinned header + scrollable body
 * + optional footer, slide-in/slide-out animation, focus trap, Escape / backdrop-click close,
 * body-scroll lock. On small screens it slides up from the bottom as a sheet with a grab
 * handle that can be dragged (or flicked) down to dismiss.
 */
export const Modal = ({ open, onClose, title, footer, children, headerAccessory, className, returnFocusTo }: Props) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Mirrored so the mounted-lifetime effect below reads the latest value without re-registering.
  const returnFocusToRef = useRef(returnFocusTo);
  returnFocusToRef.current = returnFocusTo;
  const titleId = useId();

  const [phase, setPhase] = useState<Phase>(open ? 'open' : 'closed');
  const mounted = phase !== 'closed';
  const closing = phase === 'closing';

  // This modal's place in the stack: stable per instance, registered while mounted. `covered`
  // drives the recede below and re-renders when another modal opens over / closes above this one.
  const [stackId] = useState(() => Symbol('modal'));
  const covered = useSyncExternalStore(
    subscribeStack,
    () => isCoveredModal(stackId),
    () => false,
  );

  // Drag-to-dismiss state (phone sheet only). `dragged` is sticky for the sheet's life: once
  // dragged, its transform belongs to the transition below, never to a replayed entrance.
  const isSheet = useMediaQuery(SHEET_MQ);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragged, setDragged] = useState(false);
  const dragFrom = useRef<{ y: number; at: number } | null>(null);

  // Drive the phase from the caller's `open`.
  useEffect(() => {
    if (open) {
      // A fresh open always starts undragged; a mid-exit reopen drops any drag too.
      setDragY(0);
      setDragged(false);
      setDragging(false);
      // From `closed`, place the surface below the viewport for one frame (`preEnter`) so the
      // slide-up has somewhere to come from. From `closing` (or an already-open surface), go
      // straight to `open`: the transform transitions to rest FROM wherever the exit had got
      // to, reversing smoothly instead of snapping off-screen to replay the entrance.
      setPhase((prev) => (prev === 'closed' ? 'preEnter' : 'open'));
    } else {
      setPhase((prev) => (prev === 'closed' ? 'closed' : 'closing'));
    }
  }, [open]);

  // Flip the one-frame `preEnter` start into `open` so the entrance transition runs. Two frames:
  // the first lets the browser paint the below-viewport position, the second flips to rest.
  useEffect(() => {
    if (phase !== 'preEnter') return undefined;
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setPhase('open')));
    return () => cancelAnimationFrame(r);
  }, [phase]);

  // Keep the surface mounted through the exit slide, then unmount.
  useEffect(() => {
    if (phase !== 'closing') return undefined;
    const t = setTimeout(() => setPhase('closed'), CLOSE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Focus trap + Escape (topmost only) + background scroll-lock, for the surface's whole MOUNTED
  // lifetime — not just while `open`. Keyed on `mounted` (which turns true in the same commit the
  // panel first renders, so `panelRef` is set) rather than `open` (which turns true a commit
  // earlier, before the panel exists — leaving the trap inert). Holding it until unmount also
  // keeps the scrollbar from reappearing (and shifting the page) mid-exit, and defers focus
  // restore to when the surface is actually gone.
  useEffect(() => {
    if (!mounted) return undefined;
    const restoreTo = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Don't steal focus from content that asked for it (e.g. an autoFocus field); only pull
    // focus into the dialog when it is sitting outside.
    if (panel && !panel.contains(document.activeElement)) panel.focus();

    pushModal(stackId);

    const onKey = (e: KeyboardEvent) => {
      if (!isTopModal(stackId)) return;
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    lockScroll();
    return () => {
      removeModal(stackId);
      window.removeEventListener('keydown', onKey);
      unlockScroll();
      // Prefer an explicit target (the opener may have unmounted, stranding `restoreTo` on <body>).
      (returnFocusToRef.current?.current ?? restoreTo)?.focus?.();
    };
  }, [mounted, stackId]);

  // Mark this modal as leaving so the one below rises back the moment the exit starts, while it
  // keeps its slot (and the keyboard) until it actually unmounts.
  useEffect(() => {
    if (closing) markClosing(stackId);
  }, [closing, stackId]);

  // Drag to dismiss — phone sheet only. Bound to the grab handle (not the whole sheet, so it
  // can never fight the body's own scrolling), with NATIVE listeners because the gesture takes
  // pointer capture and captured pointermoves do not reach React's delegated handlers.
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle || !isSheet || !mounted) return undefined;

    const onDown = (e: PointerEvent) => {
      // Primary pointer only: a second finger (or right-click) landing mid-gesture would reset
      // the origin and the sheet would jump.
      if (!e.isPrimary || e.button !== 0) return;
      handle.setPointerCapture(e.pointerId);
      dragFrom.current = { y: e.clientY, at: performance.now() };
      setDragging(true);
      setDragged(true);
    };
    const onMove = (e: PointerEvent) => {
      const from = dragFrom.current;
      if (!from) return;
      // Downward only: dragging up would lift the sheet off its own bottom edge.
      setDragY(Math.max(0, e.clientY - from.y));
    };
    const onUp = (e: PointerEvent) => {
      const start = dragFrom.current;
      dragFrom.current = null;
      setDragging(false);
      if (!start) return;
      const travelled = Math.max(0, e.clientY - start.y);
      const velocity = travelled / Math.max(1, performance.now() - start.at);
      // A flick still has to be a real gesture: a fast 10px twitch is not a dismissal.
      const flicked = velocity > DISMISS_VELOCITY && travelled > FLICK_MIN_PX;
      if (travelled > DISMISS_PX || flicked) {
        // Animated exit: `closing` carries the sheet on from where the finger left it, rather
        // than snapping it back to replay a keyframe.
        onCloseRef.current();
        return;
      }
      setDragY(0); // under the threshold — settle back to rest
    };

    handle.addEventListener('pointerdown', onDown);
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
    return () => {
      handle.removeEventListener('pointerdown', onDown);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      // Crossing the breakpoint mid-drag (a phone rotated into landscape) tears these down
      // before the pointerup that would settle the sheet, so put it back at rest here.
      dragFrom.current = null;
      setDragging(false);
      setDragY(0);
    };
  }, [isSheet, mounted]);

  if (!mounted) return null;

  const ownsWidth = /(^|\s)(max-w-|w-)/.test(className ?? '');

  // The sheet only ever translates: down and out when closing, tracking the finger while
  // dragged, below the viewport for the single pre-entrance frame, at rest otherwise.
  const translateY = closing
    ? '100dvh'
    : dragging || dragged
      ? `${dragY}px`
      : phase === 'preEnter'
        ? '120%'
        : '0';
  // A modal with another one on top recedes (desktop only — a stacked bottom sheet stays pinned
  // to its edge; shrinking it there just detaches it), so the stack reads as depth.
  const scale = covered && !isSheet ? COVERED_SCALE : 1;
  const sheetTransform = `translateY(${translateY}) scale(${scale})`;
  const sheetTransition = dragging
    ? 'none' // track the finger 1:1
    : closing
      ? `transform ${CLOSE_MS}ms ${EXIT_EASE}`
      : phase === 'preEnter'
        ? 'none' // placed below without animating, then flipped to rest next frame
        : `transform ${dragged ? SETTLE_MS : OPEN_MS}ms ${ENTER_EASE}`;

  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-end justify-center p-0 sm:items-center sm:p-6">
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={() => onCloseRef.current()}
        className="absolute inset-0 cursor-default bg-black/70"
        style={{
          opacity: phase === 'open' ? 1 : 0,
          transition: `opacity ${closing ? CLOSE_MS : OPEN_MS}ms ${closing ? EXIT_EASE : ENTER_EASE}`,
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex w-full flex-col overflow-hidden bg-surface-raised shadow-2xl shadow-black/50 outline-none',
          'max-h-[calc(100dvh-56px)] rounded-t-[24px]',
          'sm:max-h-[min(90dvh,calc(100dvh-3rem))] sm:rounded-card',
          !ownsWidth && 'sm:max-w-[560px]',
          className,
        )}
        style={{ transform: sheetTransform, transition: sheetTransition, willChange: 'transform' }}
      >
        {/* Grab handle — the phone sheet's drag-to-dismiss affordance; hidden as a desktop modal. */}
        <div
          ref={handleRef}
          aria-hidden="true"
          className="flex shrink-0 cursor-grab touch-none justify-center pb-0.5 pt-2.5 sm:hidden"
        >
          <span className="h-1 w-10 rounded-full bg-line-strong" />
        </div>

        {title ? (
          <div className="flex items-start justify-between gap-4 border-b border-line px-6 pb-4 pt-3 sm:pt-5">
            <div id={titleId} className="min-w-0 flex-1">
              {title}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {headerAccessory}
              <button
                type="button"
                onClick={() => onCloseRef.current()}
                aria-label="Close"
                // 48x48 tap target; the visible filled disc stays compact inside it.
                className="-mr-2 flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-ink transition-opacity hover:opacity-90"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-fg">
                  <CloseIcon />
                </span>
              </button>
            </div>
          </div>
        ) : null}
        <div className="thin-scroll flex-1 overflow-auto px-6 py-5">{children}</div>
        {footer ? <div className="border-t border-line px-6 py-4">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
